// @flow

import gql from 'graphql-tag';
import React, { type ElementRef, useEffect, useRef, useState } from 'react';
import { debounce, get, isEmpty } from 'lodash';

import { Query } from '@apollo/react-components'; // eslint-disable-line no-restricted-syntax

import AppsSearch from 'app/service/components/AppsSearch/AppsSearch';

const appsQuery = gql`
  query publicAppSearchQuery($nameContains: String!) {
    getAppsByNameContains(
      nameContains: $nameContains
      limit: 5
      excludeUpcoming: false
    ) {
      edges {
        id
        slug
        name
        images {
          url_32x32
        }
      }
    }
  }
`;

type Props = {
  className: string,
  inputRef: ElementRef<any>,
  onBlur: () => void,
  onFocus: () => void,
  onResults: (searchTerm: string, hasResults: boolean) => void,
  onSelect: (slug: string) => void,
  placeholder: string,
};

const AppSearchContainer = ({
  className,
  inputRef,
  onBlur,
  onFocus,
  onResults,
  onSelect,
  placeholder,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredItem, setHoveredItem] = useState('');

  const debounceSetSearchQuery = useRef();

  useEffect(() => {
    debounceSetSearchQuery.current = debounce(setSearchQuery, 300);

    return () => {
      if (debounceSetSearchQuery.current) {
        debounceSetSearchQuery.current.cancel();
      }
    };
  }, []);

  const onChangeSearchQuery = (query: string) => {
    setSearchText(query);

    if (debounceSetSearchQuery && debounceSetSearchQuery.current) {
      debounceSetSearchQuery.current(query);
    }
  };

  return (
    <Query
      query={appsQuery}
      skip={!isFocused}
      variables={{ nameContains: searchQuery, excludeUpcoming: false }}
    >
      {({ loading, data }) => {
        const apps = get(data, ['getAppsByNameContains', 'edges'], []);

        if (!loading && !isEmpty(searchQuery))
          onResults(searchQuery, !isEmpty(apps));

        return (
          <AppsSearch
            apps={apps}
            className={className}
            hoveredItem={hoveredItem}
            inputRef={inputRef}
            isLoading={isFocused && loading}
            onBlur={() => {
              if (isFocused) {
                setIsFocused(false);
                setHoveredItem('');
                onChangeSearchQuery('');
                onBlur();
              }
            }}
            onChange={onChangeSearchQuery}
            onFocus={() => {
              if (!isFocused) {
                setIsFocused(true);
                onFocus();
              }
            }}
            onSelect={(slug: string, didConfirm: boolean) => {
              setHoveredItem(slug);
              if (didConfirm) onSelect(slug);
            }}
            placeholder={placeholder}
            searchText={searchText}
          />
        );
      }}
    </Query>
  );
};

export default AppSearchContainer;
